import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { SessionStateCleanupStore } from '@siza-staff-care/shared/session-state-cleanup';
import { BehaviorSubject, Observable, combineLatest, interval, map, startWith, tap, withLatestFrom } from 'rxjs';

export type RequestDetail = {
	request: HttpRequest<unknown>;
	startTime: Date;
	number: number;
};

export type ResponseDetail = {
	response: HttpResponse<unknown>;
	number: number;
	timeTakenMs: number;
};

interface NetworkStatsState {
	requests: number;
	errors: number;
	retries: number;
	retriesFailed: number;
	offlineTimes: number;
	totalTimeTakenMs: number;
	longestRequestMs: number;
}

const defaultState: NetworkStatsState = {
	requests: 0,
	errors: 0,
	retries: 0,
	retriesFailed: 0,
	offlineTimes: 0,
	totalTimeTakenMs: 0,
	longestRequestMs: 0,
};

@Injectable({
	providedIn: 'root',
})
export class NetworkStatsStore extends ComponentStore<NetworkStatsState> {
	private sessionStateCleanup = inject(SessionStateCleanupStore);

	readonly requests$ = this.select((state) => state.requests);
	readonly errors$ = this.select((state) => state.errors);
	readonly retries$ = this.select((state) => state.retries);
	readonly retriesFailed$ = this.select((state) => state.retriesFailed);
	readonly offlineTimes$ = this.select((state) => state.offlineTimes);
	readonly totalTimeTakenMs$ = this.select((state) => state.totalTimeTakenMs);
	readonly longestRequestMs$ = this.select((state) => state.longestRequestMs);

	private readonly cleared$ = new BehaviorSubject<number>(0);

	readonly averageRequestLengthMs$ = this.select(this.requests$, this.totalTimeTakenMs$, (requests, totalTime) => {
		if (requests) {
			return totalTime / requests;
		}

		return 0;
	});

	readonly sessionStorageSize$ = combineLatest([this.cleared$, interval(1000).pipe(startWith(1))]).pipe(
		map(() => {
			return new Blob(Object.values(sessionStorage)).size;
		}),
	);

	readonly vm$ = this.select({
		requests: this.requests$,
		errors: this.errors$,
		retries: this.retries$,
		retriesFailed: this.retriesFailed$,
		offlineTimes: this.offlineTimes$,
		longestRequestMs: this.longestRequestMs$,
		averageRequestLengthMs: this.averageRequestLengthMs$,
		sessionStorageSize: this.sessionStorageSize$,
	});

	readonly bumpRequests = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.requests$),
			tap(([, requests]) => {
				this.patchState({ requests: requests + 1 });
			}),
		),
	);

	readonly bumpErrors = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.errors$),
			tap(([, errors]) => {
				this.patchState({ errors: errors + 1 });
			}),
		),
	);

	readonly bumpRetries = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.retries$),
			tap(([, retries]) => {
				this.patchState({ retries: retries + 1 });
			}),
		),
	);

	readonly bumpRetriesFailed = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.retriesFailed$),
			tap(([, retriesFailed]) => {
				this.patchState({ retriesFailed: retriesFailed + 1 });
			}),
		),
	);

	readonly bumpOfflineTimes = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.offlineTimes$),
			tap(([, offlineTimes]) => {
				this.patchState({ offlineTimes: offlineTimes + 1 });
			}),
		),
	);

	readonly reset = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.setState(defaultState);
			}),
		),
	);

	readonly logRequest = this.effect((request$: Observable<RequestDetail>) =>
		request$.pipe(
			tap((request) => {
				const stringified = JSON.stringify(request);
				this.sessionStateCleanup.writeRequestResponseItemToSessionStore('req', stringified);
			}),
		),
	);

	readonly logResponse = this.effect((response$: Observable<ResponseDetail>) =>
		response$.pipe(
			withLatestFrom(this.totalTimeTakenMs$, this.longestRequestMs$),
			tap(([response, totalTime, longestTime]) => {
				if (response.timeTakenMs > longestTime) {
					this.patchState({ longestRequestMs: response.timeTakenMs });
				}

				this.patchState({ totalTimeTakenMs: totalTime + response.timeTakenMs });

				const stringified = JSON.stringify(response);
				this.sessionStateCleanup.writeRequestResponseItemToSessionStore('res', stringified);
			}),
		),
	);

	readonly cleanUpSessionStorage = this.effect((maxAge$: Observable<number>) =>
		maxAge$.pipe(
			tap((maxAge) => {
				this.sessionStateCleanup.cleanUpSessionStorage(maxAge);
			}),
		),
	);

	constructor() {
		super(defaultState);
	}

	/**
	 * Returns an array of RequestDetail and ResponseDetail objects from the session storage.
	 * @returns
	 */
	dumpRequestsAndResponses() {
		const result: unknown[] = [];

		for (let i = 0; i < sessionStorage.length; i++) {
			const key = sessionStorage.key(i) ?? '';

			if (key.startsWith('req_') || key.startsWith('res_')) {
				const value = JSON.parse(sessionStorage.getItem(key) ?? '');
				if (value) {
					result.push(value);
				}
			}
		}

		return result;
	}
}
